import { logger } from "./logger";
import { DOM_TRACKING_CONFIG } from "./domTrackingConfig";

let initialized = false;

const CLICK_SELECTOR =
  'button, a, [role="button"], [data-log-id], input[type="submit"], input[type="button"]';

// Matches MUI Dialog/Modal/Drawer/Popover roots, plus the generic ARIA
// pattern any other library/hand-rolled modal would use. Extend this list
// if you introduce a modal implementation that doesn't match any of these.
const MODAL_SELECTOR =
  '[role="dialog"], [role="alertdialog"], .MuiDialog-root, .MuiModal-root, .MuiDrawer-root, .MuiPopover-root';

const SENSITIVE_FIELD_RE = /password|secret|token|otp|ssn|card.?number|cvv/i;
const MAX_VALUE_LENGTH = 200;

const truncate = (value: string | null | undefined, max = 100) =>
  value ? value.trim().slice(0, max) : undefined;

const isSensitiveField = (el: HTMLInputElement): boolean => {
  if (el.type === "password") return true;
  const probe = `${el.name || ""} ${el.id || ""} ${el.autocomplete || ""}`;
  return SENSITIVE_FIELD_RE.test(probe);
};

const getFieldIdentity = (
  el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
) => {
  const byFor =
    el.id &&
    document.querySelector(`label[for="${CSS.escape(el.id)}"]`)?.textContent;
  const label =
    byFor ||
    el.getAttribute("aria-label") ||
    el.closest("label")?.textContent ||
    (el as HTMLInputElement).placeholder;

  return {
    tag: el.tagName.toLowerCase(),
    // Nested under `details`, so this never collides with the envelope's
    // own top-level `event_type` field — e.g. "text", "email", "checkbox",
    // "select-one", "textarea" (HTMLTextAreaElement.type is always the
    // literal string "textarea").
    type: (el as HTMLInputElement).type || undefined,
    name: el.name || undefined,
    id: el.id || undefined,
    label: truncate(label, 60),
  };
};

// ---------------------------------------------------------------------------
// Clicks — buttons, links, [role="button"], data-log-id. Never logs a value.
// ---------------------------------------------------------------------------
const initClickListener = () => {
  document.addEventListener(
    "click",
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const el = target?.closest?.(CLICK_SELECTOR) as HTMLElement | null;
      if (!el) return;

      logger.action({
        message: "CLICK",
        tag: el.tagName.toLowerCase(),
        log_id: el.getAttribute("data-log-id") || undefined,
        text: truncate(el.textContent, 80),
        aria_label: truncate(el.getAttribute("aria-label"), 80),
      });
    },
    { capture: true, passive: true }
  );
};

// ---------------------------------------------------------------------------
// Inputs / selects / checkboxes / radios — one delegated "change" listener,
// branched by element type. Each branch checks its own config flag so any
// combination of these three can be toggled independently even though they
// share a single listener under the hood.
// ---------------------------------------------------------------------------
const initChangeListener = () => {
  document.addEventListener(
    "change",
    (event: Event) => {
      const el = event.target as HTMLElement | null;
      if (!el) return;

      // Native <select>, or MUI Select (renders a real hidden <input> for
      // form compatibility, sitting inside a .MuiSelect-root wrapper).
      const isMuiSelect = !!el.closest?.(".MuiSelect-root");
      if (el instanceof HTMLSelectElement || isMuiSelect) {
        if (!DOM_TRACKING_CONFIG.selectChanges) return;
        const identity = getFieldIdentity(
          el as HTMLSelectElement | HTMLInputElement
        );
        const optedOut = el.getAttribute("data-log-value") === "false";
        const value =
          el instanceof HTMLSelectElement
            ? el.selectedOptions[0]?.textContent || el.value
            : (el as HTMLInputElement).value;
        logger.action({
          message: "SELECT_CHANGE",
          ...identity,
          value: optedOut ? undefined : truncate(value, MAX_VALUE_LENGTH),
        });
        return;
      }

      if (el instanceof HTMLInputElement) {
        if (el.type === "file") {
          if (!DOM_TRACKING_CONFIG.fileInputChanges) return;
          // .value on a file input is masked by the browser to a fake path
          // (e.g. "C:\fakepath\report.jpg") for security — never useful to
          // log. Use .files instead for real name/size/type per file.
          const files = Array.from(el.files || []);
          const MAX_FILES_LOGGED = 10;
          logger.action({
            message: "FILE_INPUT_CHANGE",
            ...getFieldIdentity(el),
            file_count: files.length,
            files: files.slice(0, MAX_FILES_LOGGED).map((f) => ({
              name: f.name,
              size: f.size,
              type: f.type,
            })),
            truncated: files.length > MAX_FILES_LOGGED,
          });
          return;
        }

        if (el.type === "checkbox" || el.type === "radio") {
          if (!DOM_TRACKING_CONFIG.checkboxRadioChanges) return;
          logger.action({
            message: "CHECKBOX_RADIO_CHANGE",
            ...getFieldIdentity(el),
            checked: el.checked,
          });
          return;
        }

        if (!DOM_TRACKING_CONFIG.inputChanges) return;
        // Value is logged by default (truncated to MAX_VALUE_LENGTH) —
        // opt a specific field OUT via data-log-value="false" if its
        // content shouldn't be logged. type="password" is a hard block
        // regardless of that attribute; it can never be opted back in.
        const optedOut = el.getAttribute("data-log-value") === "false";
        const sensitive = isSensitiveField(el);
        logger.action({
          message: "INPUT_CHANGE",
          ...getFieldIdentity(el),
          value_length: el.value ? el.value.length : 0,
          value: !sensitive && !optedOut ? truncate(el.value, MAX_VALUE_LENGTH) : undefined,
        });
        return;
      }

      if (el instanceof HTMLTextAreaElement) {
        if (!DOM_TRACKING_CONFIG.inputChanges) return;
        const optedOut = el.getAttribute("data-log-value") === "false";
        logger.action({
          message: "INPUT_CHANGE",
          ...getFieldIdentity(el),
          value_length: el.value ? el.value.length : 0,
          value: optedOut ? undefined : truncate(el.value, MAX_VALUE_LENGTH),
        });
      }
    },
    { capture: true, passive: true }
  );
};

// ---------------------------------------------------------------------------
// Form submits — id/name/action only, never field contents (those are
// covered individually by the change listener above, if enabled).
// ---------------------------------------------------------------------------
const initSubmitListener = () => {
  document.addEventListener(
    "submit",
    (event: Event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      logger.action({
        message: "FORM_SUBMIT",
        id: form.id || undefined,
        name: form.getAttribute("name") || undefined,
        action: form.action || undefined,
      });
    },
    { capture: true }
  );
};

// ---------------------------------------------------------------------------
// Modal/dialog open + close — via MutationObserver instead of instrumenting
// every dialog component individually. Catches a close no matter how it
// happened (close button, Escape key, backdrop click, programmatic close).
// ---------------------------------------------------------------------------
const openModals = new WeakMap<Element, number>();

const getModalIdentity = (el: Element): string => {
  const dataLogId = el.getAttribute("data-log-id");
  if (dataLogId) return dataLogId;

  const labelledBy = el.getAttribute("aria-labelledby");
  const labelText = labelledBy && document.getElementById(labelledBy)?.textContent;
  if (labelText) return truncate(labelText, 60) as string;

  const titleEl = el.querySelector('[class*="DialogTitle"], [class*="Title"]');
  if (titleEl?.textContent) return truncate(titleEl.textContent, 60) as string;

  return el.className?.toString().split(" ")[0] || "unknown";
};

const initModalTracking = () => {
  const findModals = (node: Node): Element[] => {
    if (!(node instanceof Element)) return [];
    const self = node.matches(MODAL_SELECTOR) ? [node] : [];
    return [...self, ...Array.from(node.querySelectorAll(MODAL_SELECTOR))];
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        findModals(node).forEach((modalEl) => {
          openModals.set(modalEl, Date.now());
          logger.action({
            message: "MODAL_OPEN",
            modal: getModalIdentity(modalEl),
          });
        });
      });
      mutation.removedNodes.forEach((node) => {
        findModals(node).forEach((modalEl) => {
          const openedAt = openModals.get(modalEl);
          openModals.delete(modalEl);
          logger.action({
            message: "MODAL_CLOSE",
            modal: getModalIdentity(modalEl),
            open_duration_ms: openedAt ? Date.now() - openedAt : undefined,
          });
        });
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

// ---------------------------------------------------------------------------
// Broken image loads — {capture:true} is required here: resource-load
// "error" events do NOT bubble, so a plain document-level listener without
// capture would never see them. Capture-phase delivery happens top-down
// regardless of whether the event bubbles afterward, which is what makes
// delegation possible at all for this one.
// ---------------------------------------------------------------------------
const initImageErrorListener = () => {
  document.addEventListener(
    "error",
    (event: Event) => {
      const el = event.target;
      if (!(el instanceof HTMLImageElement)) return;
      logger.action({
        message: "IMAGE_LOAD_ERROR",
        src: truncate(el.src, 300),
        alt: truncate(el.alt, 100),
      });
    },
    { capture: true }
  );
};

// ---------------------------------------------------------------------------
// Tab visibility — separate from the visibilitychange listener in logger.ts
// (that one just flushes the buffer). This one reports how long the tab
// spent in the state it's leaving, so you can tell "watched for 20 minutes"
// apart from "open in a background tab all day".
// ---------------------------------------------------------------------------
const initTabVisibilityListener = () => {
  let lastChangeAt = Date.now();

  document.addEventListener("visibilitychange", () => {
    const now = Date.now();
    const duration = now - lastChangeAt;
    lastChangeAt = now;

    if (document.visibilityState === "hidden") {
      logger.action({ message: "TAB_HIDDEN", active_duration_ms: duration });
    } else {
      logger.action({ message: "TAB_VISIBLE", hidden_duration_ms: duration });
    }
  });
};

// ---------------------------------------------------------------------------
// Scroll depth — milestones only (25/50/75/100%), never raw scroll events.
// Milestones reset whenever the pathname changes (a new page starts back at
// 0%). Processing is rAF-throttled so it costs effectively nothing even
// though "scroll" itself fires very frequently.
// ---------------------------------------------------------------------------
const initScrollDepthListener = () => {
  const MILESTONES = [25, 50, 75, 100];
  let firedMilestones = new Set<number>();
  let lastPath = window.location.pathname;
  let ticking = false;

  const process = () => {
    ticking = false;

    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      firedMilestones = new Set();
    }

    const scrollableHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return; // page doesn't scroll, nothing to measure

    const scrolledPercent = Math.min(
      100,
      Math.round((window.scrollY / scrollableHeight) * 100)
    );

    for (const milestone of MILESTONES) {
      if (scrolledPercent >= milestone && !firedMilestones.has(milestone)) {
        firedMilestones.add(milestone);
        logger.action({
          message: "SCROLL_DEPTH",
          path: lastPath,
          milestone_percent: milestone,
        });
      }
    }
  };

  window.addEventListener(
    "scroll",
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(process);
    },
    { passive: true }
  );
};

// ---------------------------------------------------------------------------
// Paste detection — logs THAT a paste happened and how long the pasted text
// was, never the pasted content itself, regardless of any data-log-value
// attribute on the target field (that attribute only governs the change
// listener above, it's deliberately not consulted here).
// ---------------------------------------------------------------------------
const initPasteListener = () => {
  document.addEventListener(
    "paste",
    (event: ClipboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (!el) return;
      const isEditable =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el.isContentEditable;
      if (!isEditable) return;

      const pastedText = event.clipboardData?.getData("text") || "";
      logger.action({
        message: "PASTE",
        tag: el.tagName.toLowerCase(),
        name: (el as HTMLInputElement).name || undefined,
        id: el.id || undefined,
        pasted_length: pastedText.length,
      });
    },
    { capture: true }
  );
};

// ---------------------------------------------------------------------------
// Single entry point — call once from src/index.tsx. Comment out any of the
// `if` blocks below (in addition to, or instead of, flipping the matching
// flag in domTrackingConfig.ts) to remove that listener entirely.
// ---------------------------------------------------------------------------
export const initDomTracking = () => {
  if (initialized) return;
  initialized = true;

  if (DOM_TRACKING_CONFIG.clicks) {
    initClickListener();
  }

  if (
    DOM_TRACKING_CONFIG.inputChanges ||
    DOM_TRACKING_CONFIG.selectChanges ||
    DOM_TRACKING_CONFIG.checkboxRadioChanges ||
    DOM_TRACKING_CONFIG.fileInputChanges
  ) {
    initChangeListener();
  }

  if (DOM_TRACKING_CONFIG.formSubmit) {
    initSubmitListener();
  }

  if (DOM_TRACKING_CONFIG.modalOpenClose) {
    initModalTracking();
  }

  if (DOM_TRACKING_CONFIG.imageLoadErrors) {
    initImageErrorListener();
  }

  if (DOM_TRACKING_CONFIG.tabVisibility) {
    initTabVisibilityListener();
  }

  if (DOM_TRACKING_CONFIG.scrollDepth) {
    initScrollDepthListener();
  }

  if (DOM_TRACKING_CONFIG.pasteDetection) {
    initPasteListener();
  }
};
